import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import {
  User,
  Building2,
  Building,
  Lock,
  Bell,
  // FileText,   // unused while the Letter Templates tab is hidden
  Calendar,
  AlarmClock,
  UserCheck,
  Briefcase,
  Users,
  MapPin,
  // IdCard,     // unused while the Profile Generator tab is hidden
  Shield,
  LayoutDashboard,
  Image as ImageIcon,
  Menu as MenuIcon,
} from "lucide-react";
import { hasPermission, hasAnyPermission } from "../../utils/permissionUtils";

// Section order: GENERAL → SYSTEM CONFIGURATION → PERSONAL → ADMINISTRATION → APPEARANCE.
const SECTION_ORDER = [
  "General",
  "System Configuration",
  "Personal",
  "Administration",
  "Appearance",
];

const SettingsSidebar = ({ role: roleProp }) => {
  const [collapsed, setCollapsed] = useState(true);

  const role = (roleProp || localStorage.getItem("role") || "")
    .toLowerCase()
    .trim();

  const basePath =
    role === "admin"
      ? "/admin/settings"
      : role === "hr"
        ? "/hr/settings"
        : "/member/settings";

  // All settings tabs. Excluded-from-redesign tabs (Letter Templates, Profile
  // Generator, Personalise Dashboard, Backgrounds) remain listed and routable —
  // per the brief, they're kept but their inner content isn't restyled.
  const sidebarItems = [
    {
      key: "companyinfo",
      label: "Company Information",
      Icon: Building2,
      category: "General",
      permission: "manage_company_info",
      path: "company-info",
    },
    {
      key: "password",
      label: "Password",
      Icon: Lock,
      category: "General",
      permission: "view_settings",
      path: "password-management",
    },
    // Letter Templates — hidden from the sidebar. The route and page are left
    // untouched, so the tab is still reachable by URL; only the nav entry is
    // gone. Restore by un-commenting this block (and its FileText import).
    // {
    //   key: "lettertemplates",
    //   label: "Letter Templates",
    //   Icon: FileText,
    //   category: "General",
    //   permission: "manage_letter_templates",
    //   path: "letter-templates",
    //   roles: ["admin", "hr"],
    // },

    {
      key: "calendars",
      label: "Calendar Settings",
      Icon: Calendar,
      category: "System Configuration",
      // Holidays tab, Leave Types tab, GreytHR Sync tab — any key opens the page.
      permission: ["manage_calendar_settings", "manage_leave_types", "manage_greythr_sync"],
      path: "calendar",
    },
    {
      key: "shift",
      label: "Shift Management",
      Icon: AlarmClock,
      category: "System Configuration",
      permission: "manage_calendar_settings",
      path: "shift-management",
    },
    {
      key: "attendance",
      label: "Attendance Settings",
      Icon: UserCheck,
      category: "System Configuration",
      permission: "manage_calendar_settings",
      path: "attendance",
    },
    {
      key: "departments",
      label: "Departments",
      Icon: Building,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "departments",
    },
    {
      key: "jobtitles",
      label: "Job Titles",
      Icon: Briefcase,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "job-titles",
    },
    {
      key: "employeetypes",
      label: "Employee Types",
      Icon: Users,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "employee-types",
    },
    {
      key: "worklocations",
      label: "Work Locations",
      Icon: MapPin,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "work-locations",
    },
    {
      key: "employmentstatuses",
      label: "Employment Statuses",
      Icon: User,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "employment-statuses",
    },
    // Profile Generator — hidden from the sidebar, same as Letter Templates
    // above: route and page stay, only the nav entry is removed. Restore by
    // un-commenting this block (and its IdCard import).
    // {
    //   key: "profilepicgenerator",
    //   label: "Profile Generator",
    //   Icon: IdCard,
    //   category: "System Configuration",
    //   permission: "manage_profile_generator",
    //   path: "profile-generator",
    //   roles: ["admin", "hr"],
    // },
    {
      key: "notifications",
      label: "Notifications",
      Icon: Bell,
      category: "Personal",
      permission: "view_settings",
      path: "notifications",
    },
    {
      key: "rolesacess",
      label: "Roles and Permissions",
      Icon: Shield,
      category: "Administration",
      permission: "manage_roles_permissions",
      path: "roles-access",
    },

    {
      key: "personalise",
      label: "Personalise Dashboard",
      Icon: LayoutDashboard,
      category: "Appearance",
      permission: "view_settings",
      path: "personalise",
    },
    {
      key: "backgrounds",
      label: "Backgrounds",
      Icon: ImageIcon,
      category: "Appearance",
      // No permission — everyone can upload and manage their own images.
      permission: null,
      path: "backgrounds",
    },
  ];

  // Permission alone decides what is listed. Each item used to carry a second
  // `roles: ["admin","hr"]` gate, which meant a custom role granted the
  // permission still saw nothing — a role arrives here as "member" whatever
  // Roles & Access says. Admin short-circuits inside hasPermission.
  // `permission: null` = open to every signed-in user.
  const visibleItems = sidebarItems.filter((item) =>
    !item.permission
      ? true
      : Array.isArray(item.permission)
        ? hasAnyPermission(item.permission)
        : hasPermission(item.permission),
  );

  const grouped = visibleItems.reduce((acc, item) => {
    (acc[item.category] ||= []).push(item);
    return acc;
  }, {});

  const sections = SECTION_ORDER.filter((c) => grouped[c]?.length);

  return (
    <nav className={`em-settings-sidebar ${collapsed ? "is-collapsed" : ""}`}>
      <button
        type="button"
        className="em-settings-mobtoggle"
        onClick={() => setCollapsed((c) => !c)}
      >
        <span>Settings Menu</span>
        <MenuIcon size={18} />
      </button>

      <div className="em-settings-sectionwrap">
        {sections.map((category) => (
          <div key={category} className="em-settings-section">
            <div className="em-settings-section-label">{category}</div>
            {grouped[category].map((item) => (
              <NavLink
                key={item.key}
                to={`${basePath}/${item.path}`}
                onClick={() => setCollapsed(true)}
                className={({ isActive }) =>
                  `em-settings-navitem ${isActive ? "active" : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.Icon size={15} style={{ flexShrink: 0 }} />
                    <span className="em-settings-navlabel">{item.label}</span>
                    {isActive && <span className="em-settings-navdot" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
};

SettingsSidebar.propTypes = {
  role: PropTypes.string,
};

export default SettingsSidebar;
